// src/components/ErrorBoundary.tsx
//
// Generic React error boundary. Catches rendering errors in its subtree,
// reports them to our logger + PostHog, and renders a minimal fallback UI
// instead of blanking the taskpane.
//
// Primary use case today: wrapping <AuthProvider> so an error during auth
// bootstrap surfaces a diagnosable signal (both in logs and in PostHog) rather
// than vanishing silently.

import React from "react";
import { createLogger } from "../utils/logger";
import { track } from "../utils/analytics";

const log = createLogger("ErrorBoundary");

interface ErrorBoundaryProps {
  /** Label identifying where this boundary is in the tree (used in telemetry). */
  name: string;
  /** Optional fallback UI; defaults to a minimal error message. */
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      errorMessage: error instanceof Error ? error.message : String(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const errorMessage = error instanceof Error ? error.message : String(error);
    log.error(`Caught rendering error in "${this.props.name}"`, {
      error: errorMessage,
      componentStack: errorInfo.componentStack,
    });
    track("app:error_boundary_caught", {
      boundary: this.props.name,
      error_message: errorMessage,
      // componentStack is a multi-line string; truncate to keep payload small
      component_stack: errorInfo.componentStack?.slice(0, 500),
    });
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }
      return (
        <div
          role="alert"
          style={{
            padding: "16px",
            fontFamily: "Segoe UI, sans-serif",
            color: "#a80000",
          }}
        >
          <h3 style={{ margin: "0 0 8px 0" }}>Something went wrong</h3>
          <p style={{ margin: 0 }}>
            Contract.Space ran into an unexpected error. Please reload the task pane.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
